// Cache central + pub/sub de validacion.json.
//
// Esquema persistido en SharePoint
//   AFAC/Documentación para Consultoria/validacion.json
// ─────────────────────────────────────────────────────────────────────────
// {
//   "version": "1.0",
//   "fecha_inicial": "ISO",
//   "documentos": {
//     "{driveItemId}": {
//       "estado": "Aprobado | Pendiente | Requiere ajuste | Bloqueado | Obsoleto",
//       "notas": "string",
//       "ultimaModificacion": "ISO",
//       "modificadoPor": "Franco Lazzarini",
//       "modificadoPorEmail": "flazzarini@jmacybersec.com",
//       "filename": "JMA_AFAC_....docx",
//       "relativePath": "AFAC/Documentación para Consultoria/...",
//       "tags": [],
//       "historial": [
//         { "fecha": "ISO", "estado_anterior": "...", "estado_nuevo": "...",
//           "nota": "...", "modificadoPor": "...", "modificadoPorEmail": "..." }
//       ]
//     }
//   },
//   "clientes": {
//     "<clienteId>": {
//       "hitos": [
//         {
//           "id": "hito-<timestamp>-<random>",
//           "descripcion": "string",
//           "fechaObjetivo": "YYYY-MM-DD" | null,
//           "responsable": "string",
//           "creado": "ISO",
//           "creadoPor": "string",
//           "completado": boolean,
//           "completadoEn": "ISO" | null
//         }
//       ]
//     }
//   }
// }
//
// La clave es el driveItemId de Microsoft Graph (estable ante renombrados
// o movimientos dentro del drive). filename y relativePath se persisten
// dentro de la entry para que Cowork pueda iterar por entries sin
// resolver IDs.
//
// Estrategia de persistencia:
//   - UI optimista: muta cache en memoria + emit() inmediato.
//   - Intenta PUT a SharePoint.
//   - Si falla: encola { snapshot, previousSnapshot } en IndexedDB.
//   - Scheduler interno reintenta cada 30s; cada intento usa el
//     nextRetryAt con backoff exponencial.
//   - Tras MAX_RETRIES fallos: rollback al previousSnapshot, toast error.

import {
  loadValidationJson,
  saveValidationJson
} from '../graph/validation-writer.js'
import {
  enqueueChange,
  getReadyChanges,
  updateChange,
  removeChange,
  pendingCount,
  computeNextRetryAt,
  MAX_RETRIES
} from './validation-queue.js'
import { showToast } from './toast-bus.js'

const ESTADOS_VALIDOS = new Set([
  'Aprobado',
  'Pendiente',
  'Requiere ajuste',
  'Bloqueado',
  'Obsoleto'
])

const SCHEDULER_INTERVAL_MS = 30000

let cache = null
let initialized = false
let initPromise = null
let scheduler = null
const listeners = new Set()

function emit() {
  for (const cb of listeners) {
    try {
      cb(cache)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[validation-store] listener falló:', err)
    }
  }
}

function emptyState() {
  return {
    version: '1.0',
    fecha_inicial: new Date().toISOString(),
    documentos: {},
    clientes: {}
  }
}

function normalize(remote) {
  const safe = remote && typeof remote === 'object' ? remote : {}
  return {
    version: safe.version || '1.0',
    fecha_inicial: safe.fecha_inicial || new Date().toISOString(),
    documentos:
      safe.documentos && typeof safe.documentos === 'object'
        ? safe.documentos
        : {},
    clientes:
      safe.clientes && typeof safe.clientes === 'object' ? safe.clientes : {}
  }
}

function ensureScheduler() {
  if (scheduler) return
  if (typeof window === 'undefined') return
  scheduler = window.setInterval(() => {
    flushQueue({ silent: true }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[validation-store] scheduler flush falló:', err)
    })
  }, SCHEDULER_INTERVAL_MS)
}

/**
 * Inicializa el cache. Idempotente.
 */
export function initValidation() {
  if (initialized) return Promise.resolve(cache)
  if (initPromise) return initPromise

  initPromise = (async () => {
    try {
      const remote = await loadValidationJson()
      if (remote == null) {
        cache = emptyState()
        try {
          await saveValidationJson(cache)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[validation-store] No se pudo crear validacion.json inicial:', err)
        }
      } else {
        cache = normalize(remote)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[validation-store] Error cargando validacion.json:', err)
      cache = emptyState()
    }
    initialized = true
    await flushQueue({ silent: true })
    ensureScheduler()
    emit()
    return cache
  })()

  return initPromise
}

export function getCache() {
  return cache
}

/**
 * @param {{ id?: string, driveItemId?: string }} file
 */
export function getDocumentEntry(file) {
  if (!cache || !file) return null
  const id = file.id || file.driveItemId
  if (!id) return null
  return cache.documentos[id] || null
}

export function subscribeValidation(callback) {
  listeners.add(callback)
  return () => listeners.delete(callback)
}

/**
 * Actualiza el estado de un documento. Aplica UI optimista; si Graph
 * falla, encola para reintento con backoff exponencial.
 *
 * @param {object} file Item de Graph (id, name, relativePath, webUrl…)
 * @param {{ estado: string, nota?: string, modificadoPor?: string }} payload
 */
export async function setDocumentState(file, payload) {
  if (!cache) await initValidation()
  if (!file?.id) throw new Error('[validation-store] file.id (driveItemId) requerido.')
  const {
    estado,
    nota = '',
    modificadoPor = 'Desconocido',
    modificadoPorEmail = ''
  } = payload || {}
  if (!ESTADOS_VALIDOS.has(estado)) {
    throw new Error(`[validation-store] Estado inválido: "${estado}".`)
  }

  const driveItemId = file.id
  const prev = cache.documentos[driveItemId] || null
  const previousSnapshot = prev
    ? {
        ...prev,
        historial: Array.isArray(prev.historial) ? [...prev.historial] : []
      }
    : null

  const now = new Date().toISOString()
  const updated = {
    estado,
    notas: String(nota || ''),
    ultimaModificacion: now,
    modificadoPor,
    modificadoPorEmail,
    filename: file.name,
    relativePath: file.relativePath,
    tags: Array.isArray(prev?.tags) ? prev.tags : [],
    historial: [
      ...(Array.isArray(prev?.historial) ? prev.historial : []),
      {
        fecha: now,
        estado_anterior: prev?.estado || null,
        estado_nuevo: estado,
        nota: String(nota || ''),
        modificadoPor,
        modificadoPorEmail
      }
    ]
  }

  cache.documentos[driveItemId] = updated
  emit()

  try {
    await saveValidationJson(cache)
    showToast({ message: `Estado guardado: ${estado}`, kind: 'success' })
    return { ok: true, queued: false }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[validation-store] Falló PUT, encolando con backoff:', err)
    await enqueueChange({ driveItemId, snapshot: updated, previousSnapshot })
    showToast({
      message:
        'Sin conexión con SharePoint. Cambio guardado localmente; se reintentará automáticamente.',
      kind: 'warning',
      duration: 5000
    })
    ensureScheduler()
    return { ok: false, queued: true, error: err }
  }
}

/**
 * Reaplica los cambios encolados que estén "ready" (nextRetryAt vencido).
 * Si un PUT vuelve a fallar:
 *   - retries < MAX_RETRIES: incrementa retries y reprograma nextRetryAt.
 *   - retries >= MAX_RETRIES: rollback al previousSnapshot, drop del item.
 *
 * Procesa de forma serial: si un item falla, detiene el bucle para no
 * desperdiciar peticiones a Graph cuando ya hay un fallo confirmado.
 */
export async function flushQueue({ silent = false } = {}) {
  if (!cache) cache = emptyState()
  const ready = await getReadyChanges()
  if (ready.length === 0) {
    return { applied: 0, rolledBack: 0, remaining: await pendingCount() }
  }

  let applied = 0
  let rolledBack = 0

  for (const item of ready) {
    if (item.snapshot && item.driveItemId) {
      cache.documentos[item.driveItemId] = item.snapshot
    }

    try {
      await saveValidationJson(cache)
      await removeChange(item.id)
      applied += 1
    } catch (err) {
      const nextRetries = (item.retries || 0) + 1
      if (nextRetries >= MAX_RETRIES) {
        if (item.previousSnapshot && item.driveItemId) {
          cache.documentos[item.driveItemId] = item.previousSnapshot
        } else if (item.driveItemId) {
          delete cache.documentos[item.driveItemId]
        }
        await removeChange(item.id)
        rolledBack += 1
        // eslint-disable-next-line no-console
        console.error(
          `[validation-store] Rollback tras ${MAX_RETRIES} reintentos en driveItemId=${item.driveItemId}:`,
          err
        )
      } else {
        await updateChange(item.id, {
          retries: nextRetries,
          nextRetryAt: computeNextRetryAt(nextRetries)
        })
        // eslint-disable-next-line no-console
        console.warn(
          `[validation-store] Retry ${nextRetries}/${MAX_RETRIES} programado para driveItemId=${item.driveItemId}`
        )
        break
      }
    }
  }

  const remaining = await pendingCount()
  if (applied > 0 || rolledBack > 0) {
    emit()
    if (!silent) {
      if (rolledBack > 0) {
        showToast({
          message: `${rolledBack} cambio(s) no pudieron persistirse tras ${MAX_RETRIES} reintentos. Se revirtieron localmente.`,
          kind: 'error',
          duration: 6000
        })
      }
      if (applied > 0) {
        showToast({
          message:
            remaining === 0
              ? `Cambios offline sincronizados: ${applied}.`
              : `Sincronizados ${applied}, pendientes ${remaining}.`,
          kind: remaining === 0 ? 'success' : 'warning'
        })
      }
    }
  }
  return { applied, rolledBack, remaining }
}

/**
 * Detiene el scheduler interno. Útil al cerrar sesión.
 */
export function stopValidation() {
  if (scheduler) {
    window.clearInterval(scheduler)
    scheduler = null
  }
}

// ---------------------------------------------------------------------------
// API de hitos por cliente
//
// El esquema de `cache.clientes[clienteId].hitos[]` se persiste junto al
// resto de validacion.json en cada mutación. A diferencia del estado de
// documentos, los hitos NO usan la cola de IndexedDB ni el backoff: la
// frecuencia esperada de cambios es baja, los volúmenes pequeños y los
// dos revisores se coordinan humanamente. Si Graph falla, se aplica el
// cambio en memoria y se muestra un toast de aviso; al recargar la
// página el último estado remoto se restaura.
// ---------------------------------------------------------------------------

function generateHitoId() {
  return `hito-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function ensureClienteEntry(clienteId) {
  if (!cache.clientes[clienteId]) cache.clientes[clienteId] = { hitos: [] }
  if (!Array.isArray(cache.clientes[clienteId].hitos)) {
    cache.clientes[clienteId].hitos = []
  }
  return cache.clientes[clienteId]
}

export function getClienteState(clienteId) {
  if (!cache || !clienteId) return { hitos: [] }
  const cli = cache.clientes[clienteId] || { hitos: [] }
  return {
    ...cli,
    hitos: Array.isArray(cli.hitos) ? cli.hitos : []
  }
}

/**
 * Snapshot deep del subárbol `cache.clientes` para rollback en caso de
 * fallo al persistir hitos. Es pequeño (un nodo por cliente con su
 * array de hitos), por lo que el costo de JSON-clone es despreciable.
 */
function snapshotClientes() {
  return JSON.parse(JSON.stringify(cache.clientes || {}))
}

/**
 * Persiste el cache completo en SharePoint y, si falla, restaura
 * `cache.clientes` al snapshot previo (rollback), notifica y emite.
 */
async function commitClientesChange(actionLabel, beforeSnapshot) {
  try {
    await saveValidationJson(cache)
    showToast({ message: `${actionLabel}.`, kind: 'success' })
    return { ok: true }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      `[validation-store] ${actionLabel} no se pudo persistir, rollback:`,
      err
    )
    cache.clientes = beforeSnapshot
    emit()
    showToast({
      message: `${actionLabel} no se pudo guardar en SharePoint. Cambio revertido.`,
      kind: 'warning',
      duration: 5000
    })
    return { ok: false, error: err }
  }
}

/**
 * Agrega un hito al cliente indicado.
 * @param {string} clienteId
 * @param {object} payload { descripcion, fechaObjetivo, responsable }
 * @param {object} [meta] { creadoPor, creadoPorEmail }
 */
// Importante: TODAS las mutaciones de hitos reemplazan la referencia del
// array `cli.hitos` con uno nuevo (vía spread o filter). Las mutaciones
// in-place rompían el shallow-compare de Preact y dejaban la UI sin
// re-renderizar (bug detectado el 2026-05-19 al togglear completado).
export async function addHito(clienteId, payload, meta = {}) {
  if (!cache) await initValidation()
  if (!clienteId) throw new Error('[validation-store] clienteId requerido.')
  const descripcion = String(payload?.descripcion || '').trim()
  if (!descripcion) throw new Error('[validation-store] descripcion requerida.')

  const before = snapshotClientes()
  const now = new Date().toISOString()
  const cli = ensureClienteEntry(clienteId)
  const hito = {
    id: generateHitoId(),
    descripcion,
    fechaObjetivo: payload?.fechaObjetivo || null,
    responsable: String(payload?.responsable || '').trim(),
    creado: now,
    creadoPor:
      meta?.creadoPor ||
      meta?.creadoPorEmail ||
      'Desconocido',
    completado: false,
    completadoEn: null
  }
  cli.hitos = [...cli.hitos, hito]
  emit()
  const result = await commitClientesChange('Hito agregado', before)
  return result.ok ? hito : null
}

export async function updateHito(clienteId, hitoId, patch) {
  if (!cache) await initValidation()
  const cli = cache.clientes?.[clienteId]
  if (!cli || !Array.isArray(cli.hitos)) return null
  const idx = cli.hitos.findIndex((h) => h.id === hitoId)
  if (idx < 0) return null
  const before = snapshotClientes()
  const updated = { ...cli.hitos[idx], ...patch }
  cli.hitos = [
    ...cli.hitos.slice(0, idx),
    updated,
    ...cli.hitos.slice(idx + 1)
  ]
  emit()
  const result = await commitClientesChange('Hito actualizado', before)
  return result.ok ? updated : null
}

export async function toggleHitoCompletado(clienteId, hitoId) {
  if (!cache) await initValidation()
  const cli = cache.clientes?.[clienteId]
  if (!cli || !Array.isArray(cli.hitos)) return null
  const idx = cli.hitos.findIndex((h) => h.id === hitoId)
  if (idx < 0) return null
  const before = snapshotClientes()
  const wasCompletado = !!cli.hitos[idx].completado
  const updated = {
    ...cli.hitos[idx],
    completado: !wasCompletado,
    completadoEn: !wasCompletado ? new Date().toISOString() : null
  }
  cli.hitos = [
    ...cli.hitos.slice(0, idx),
    updated,
    ...cli.hitos.slice(idx + 1)
  ]
  emit()
  const result = await commitClientesChange(
    wasCompletado ? 'Hito reabierto' : 'Hito marcado como completado',
    before
  )
  return result.ok ? updated : null
}

export async function removeHito(clienteId, hitoId) {
  if (!cache) await initValidation()
  const cli = cache.clientes?.[clienteId]
  if (!cli || !Array.isArray(cli.hitos)) return false
  if (!cli.hitos.some((h) => h.id === hitoId)) return false
  const before = snapshotClientes()
  cli.hitos = cli.hitos.filter((h) => h.id !== hitoId)
  emit()
  const result = await commitClientesChange('Hito eliminado', before)
  return result.ok
}
